<template>
	<section class="spv-details">
		<SmallLoader v-if="loading" />
		<template v-else>
			<!-- SPV Exists and Pending Review -->
			<div v-if="spvId">
				<div class="spv-details__pending" v-if="spvInfo && spvInfo.status === 'Pending'">
					<div class="spv-details__pending-icon">
						<svg width="32" height="32" viewBox="0 0 24 24" fill="none">
							<path d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
							<path d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
						</svg>
					</div>
					<h3 class="spv-details__pending-title">SPV Under Review</h3>
					<p class="spv-details__pending-text">
						The SPV attached to this deal is currently being reviewed by our team. 
						To make changes, please contact support.
					</p>
					<a href="mailto:support@getequity.io" class="spv-details__pending-btn">
						<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
							<path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
							<path d="M22 6L12 13L2 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
						</svg>
						Contact Support
					</a>
				</div>
				
				<!-- SPV Uploaded/Active -->
				<div class="spv-details__info" v-else-if="spvInfo && spvInfo.status === 'Uploaded'">
					<div class="spv-details__info-header">
						<div class="spv-details__info-icon">
							<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
								<path d="M19 21L12 16L5 21V5C5 4.46957 5.21071 3.96086 5.58579 3.58579C5.96086 3.21071 6.46957 3 7 3H17C17.5304 3 18.0391 3.21071 18.4142 3.58579C18.7893 3.96086 19 4.46957 19 5V21Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
							</svg>
						</div>
						<div class="spv-details__info-title-wrap">
							<h3 class="spv-details__info-name">{{ spvInfo.name }}</h3>
							<span class="spv-details__info-type">{{ managementType }}</span>
						</div>
						<span class="spv-details__status spv-details__status--active">
							<span class="spv-details__status-dot"></span>
							Uploaded
						</span>
					</div>
					
					<div class="spv-details__info-grid">
						<div class="spv-details__info-item">
							<span class="spv-details__info-label">Country of Incorporation</span>
							<span class="spv-details__info-value">{{ spvInfo.country }}</span>
						</div>
						<div class="spv-details__info-item">
							<span class="spv-details__info-label">Tax Identification Number</span>
							<span class="spv-details__info-value">{{ spvInfo.tin }}</span>
						</div>
						<div class="spv-details__info-item">
							<span class="spv-details__info-label">Date of Creation</span>
							<span class="spv-details__info-value">{{ formatISODate(spvInfo.createdAt) }}</span>
						</div>
					</div>
					
					<div class="spv-details__documents">
						<h4 class="spv-details__documents-title">Documents</h4>
						<DocumentViewer :doc="spvInfo" />
					</div>
				</div>
			</div>
			
			<!-- No SPV Attached -->
			<div v-else class="spv-details__empty">
				<div class="spv-details__empty-icon">
					<svg width="48" height="48" viewBox="0 0 24 24" fill="none">
						<path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
						<path d="M14 2V8H20" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
						<path d="M12 18V12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
						<path d="M9 15H15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>
				</div>
				<h3 class="spv-details__empty-title">No SPV Attached</h3>
				<p class="spv-details__empty-text">
					This deal doesn't have an SPV attached yet. Upload your SPV documents or contact support to request one.
				</p>
				<div class="spv-details__empty-actions">
					<button class="spv-details__btn spv-details__btn--primary" @click="showUploadModal = true">
						<svg width="18" height="18" viewBox="0 0 24 24" fill="none">
							<path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
							<path d="M17 8L12 3L7 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
							<path d="M12 3V15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
						</svg>
						Upload SPV
					</button>
					<a href="mailto:support@getequity.io" class="spv-details__btn spv-details__btn--ghost">
						<svg width="18" height="18" viewBox="0 0 24 24" fill="none">
							<path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
							<path d="M22 6L12 13L2 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
						</svg>
						Request SPV
					</a>
				</div>
			</div>
			
			<SpvUploadModal
				v-if="showUploadModal"
				@close-modal="showUploadModal = false"
				@show-success-modal="addSpvToDeal"
			/>
			<SpvLinkSuccess
				v-if="showSuccessModal"
				@close-modal="showSuccessModal = false"
			/>
		</template>
	</section>
</template>

<script setup>
import DocumentViewer from "@/components/general/DocumentViewer.vue";
import SpvLinkSuccess from "@/components/spv/SpvLinkSuccess.vue";
import { formatISODate } from "@/utils/helpers";
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import SpvUploadModal from "./SpvUploadModal.vue";

const props = defineProps({
	spvId: {
		type: String,
		required: false,
		default: () => "",
	},
});
const route = useRoute();
const store = useStore();

const loading = computed(() => store.getters["loading"]);
const spvId = computed(() => props.spvId);
const spvInfo = computed(() => store.getters["Spv/spvInfo"]);
const orgName = computed(
	() => store.state.organisationModule.organisationDetails.name
);
const managementType = computed(() =>
	spvInfo.value?.should_manage_spv
		? "Managed by GetEquity"
		: `Managed by ${orgName.value}`
);

const showUploadModal = ref(false);
const showSuccessModal = ref(false);

const addSpvToDeal = async (response) => {
	const spv = response.data.data;
	const spvId = spv._id;
	const id = route.params.id;

	try {
		await store.dispatch("TokenModule/updateTokenRequest", {
			id,
			deal: { spv: spvId },
		});

		await Promise.all([
			store.dispatch("TokenModule/getTokenRequest", id),
			getSpvInfo(spvId),
		]);
		showUploadModal.value = false;
		showSuccessModal.value = true;
	} catch (error) {
		return error;
	}
};
const getSpvInfo = async (spv) => {
	await store.dispatch("Spv/fetchSpvRequest", spvId.value || spv);
};
if (spvId.value) {
	getSpvInfo();
}

</script>

<style lang="scss" scoped>
.spv-details {
	font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
	
	// Pending State
	&__pending {
		max-width: 400px;
		margin: 0 auto;
		text-align: center;
		padding: 40px 20px;
	}
	
	&__pending-icon {
		width: 72px;
		height: 72px;
		margin: 0 auto 20px;
		display: flex;
		align-items: center;
		justify-content: center;
		background: rgba(245, 158, 11, 0.1);
		border-radius: 50%;
		color: #D97706;
	}
	
	&__pending-title {
		font-size: 18px;
		font-weight: 500;
		color: #0F172A;
		margin: 0 0 8px 0;
	}
	
	&__pending-text {
		font-size: 14px;
		color: #64748B;
		line-height: 1.6;
		margin: 0 0 24px 0;
	}
	
	&__pending-btn {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		padding: 12px 24px;
		background: rgba(245, 158, 11, 0.1);
		color: #D97706;
		border-radius: 8px;
		font-size: 14px;
		font-weight: 500;
		text-decoration: none;
		transition: all 0.2s ease;
		
		&:hover {
			background: rgba(245, 158, 11, 0.15);
		}
	}
	
	// SPV Info
	&__info {
		background: #F8FAFC;
		border: 1px solid #E2E8F0;
		border-radius: 12px;
		overflow: hidden;
	}
	
	&__info-header {
		display: flex;
		align-items: center;
		gap: 16px;
		padding: 24px;
		background: #ffffff;
		border-bottom: 1px solid #E2E8F0;
	}
	
	&__info-icon {
		width: 48px;
		height: 48px;
		display: flex;
		align-items: center;
		justify-content: center;
		background: linear-gradient(135deg, #4D84FF 0%, #6B9AFF 100%);
		border-radius: 12px;
		color: #ffffff;
		flex-shrink: 0;
	}
	
	&__info-title-wrap {
		flex: 1;
	}
	
	&__info-name {
		font-size: 18px;
		font-weight: 500;
		color: #0F172A;
		margin: 0 0 4px 0;
	}
	
	&__info-type {
		font-size: 13px;
		color: #4D84FF;
	}
	
	&__status {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 6px 12px;
		border-radius: 20px;
		font-size: 12px;
		font-weight: 500;
		
		&--active {
			background: rgba(16, 185, 129, 0.1);
			color: #059669;
		}
	}
	
	&__status-dot {
		width: 6px;
		height: 6px;
		background: currentColor;
		border-radius: 50%;
	}
	
	&__info-grid {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 24px;
		padding: 24px;
		
		@media (max-width: 768px) {
			grid-template-columns: 1fr;
			gap: 16px;
		}
	}
	
	&__info-item {
		display: flex;
		flex-direction: column;
		gap: 6px;
	}
	
	&__info-label {
		font-size: 12px;
		color: #64748B;
		text-transform: uppercase;
		letter-spacing: 0.05em;
	}
	
	&__info-value {
		font-size: 15px;
		font-weight: 500;
		color: #0F172A;
	}
	
	&__documents {
		padding: 24px;
		border-top: 1px solid #E2E8F0;
		background: #ffffff;
	}
	
	&__documents-title {
		font-size: 14px;
		font-weight: 500;
		color: #0F172A;
		margin: 0 0 16px 0;
	}
	
	// Empty State
	&__empty {
		max-width: 400px;
		margin: 0 auto;
		text-align: center;
		padding: 60px 20px;
	}
	
	&__empty-icon {
		width: 88px;
		height: 88px;
		margin: 0 auto 24px;
		display: flex;
		align-items: center;
		justify-content: center;
		background: #F1F5F9;
		border-radius: 50%;
		color: #94A3B8;
	}
	
	&__empty-title {
		font-size: 20px;
		font-weight: 500;
		color: #0F172A;
		margin: 0 0 8px 0;
	}
	
	&__empty-text {
		font-size: 14px;
		color: #64748B;
		line-height: 1.6;
		margin: 0 0 32px 0;
	}
	
	&__empty-actions {
		display: flex;
		flex-direction: column;
		gap: 12px;
		align-items: center;
	}
	
	// Buttons
	&__btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
		padding: 14px 28px;
		border: none;
		border-radius: 10px;
		font-size: 14px;
		font-weight: 500;
		cursor: pointer;
		transition: all 0.2s ease;
		text-decoration: none;
		min-width: 180px;
		
		&--primary {
			background: #4D84FF;
			color: #ffffff;
			
			&:hover {
				background: #3B6EE8;
				transform: translateY(-1px);
			}
		}
		
		&--ghost {
			background: transparent;
			color: #4D84FF;
			
			&:hover {
				background: rgba(77, 132, 255, 0.08);
			}
		}
	}
}
</style>
